// =========================================================================
// RingGauge — a percentage dial drawn as two SVG circles. Fork-owned (zone C).
//
// Port of `ring(pct, st)` at line 2364 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`.
//
// Geometry is verbatim: an 86 × 86 box, radius 34, 7px stroke, the arc
// dash-offset trick (`dasharray = c × pct/100, c`) and the `rotate(-90 43 43)`
// that starts the sweep at twelve o'clock. The demo's three states map onto
// the same three tokens.
//
// One addition: the demo printed the percentage twice — once zero-padded in
// the centre, once again as `NN%` underneath. The caption line is kept but is
// now a `label` prop, defaulting to the demo's `NN%`, so the alert page can
// say what the number is a percentage *of*.
//
// UI layer — renders output, no data access.
// =========================================================================

/** The demo's three arc states. */
export type RingTone = 'done' | 'now' | 'idle';

export type RingGaugeProps = {
  /** 0–100. Values outside the range are clamped. */
  readonly percent: number;
  /** `'done'` → good, `'now'` → warn, `'idle'` → hairline. Default `'now'`. */
  readonly tone?: RingTone;
  /** Caption under the figure. Defaults to the demo's `NN%`. */
  readonly label?: string;
  /** Accessible name. Falls back to `<percent> per cent`. */
  readonly ariaLabel?: string;
  readonly className?: string;
};

const RADIUS = 34;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const TONE_COLOR: Record<RingTone, string> = {
  done: 'var(--good)',
  now: 'var(--warn)',
  idle: 'var(--hairline-strong)',
};

export function RingGauge({
  percent,
  tone = 'now',
  label,
  ariaLabel,
  className,
}: RingGaugeProps) {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  const arc = (CIRCUMFERENCE * pct) / 100;

  return (
    <svg
      viewBox="0 0 86 86"
      role="img"
      aria-label={ariaLabel ?? `${pct} per cent`}
      className={className}
    >
      <circle
        cx="43"
        cy="43"
        r={RADIUS}
        fill="none"
        stroke="var(--hairline)"
        strokeWidth="7"
      />
      <circle
        cx="43"
        cy="43"
        r={RADIUS}
        fill="none"
        stroke={TONE_COLOR[tone]}
        strokeWidth="7"
        strokeDasharray={`${arc} ${CIRCUMFERENCE}`}
        strokeLinecap="round"
        transform="rotate(-90 43 43)"
      />
      <text
        x="43"
        y="40"
        textAnchor="middle"
        fontSize="15"
        fontWeight="600"
        fontFamily={MONO}
        fill="currentColor"
      >
        {String(pct).padStart(2, '0')}
      </text>
      <text
        x="43"
        y="54"
        textAnchor="middle"
        fontSize="9"
        fontFamily={MONO}
        fill="var(--ink-tertiary)"
      >
        {label ?? `${pct}%`}
      </text>
    </svg>
  );
}
